import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

interface PieChartData {
  label: string;
  value: number;
  color?: string;
}

interface PieChartProps {
  data: PieChartData[];
  width?: number;
  height?: number;
}

export function PieChart({ data, width = 160, height = 160 }: PieChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // Clear previous chart

    // Filter out "combined" data (case insensitive) and get combined value
    const combinedItem = data.find(d => d.label.toLowerCase() === 'combined');
    const filteredData = data.filter(d => d.label.toLowerCase() !== 'combined');
    
    // If no data after filtering, return early
    if (filteredData.length === 0) return;

    // Calculate radius proportional to combined value
    // Scale combined value (0.8-1.0) to radius multiplier (0.6-1.0)
    const baseRadius = Math.min(width, height) / 1.2;
    const minCombined = 0.7;
    const maxCombined = 1.0;
    const combinedValue = Math.min(maxCombined, Math.max(minCombined, combinedItem?.value || 1.0));
    const minRadiusScale = 0.2;
    const maxRadiusScale = 0.6;
    
    // Linear interpolation: map [0.8, 1.0] to [0.6, 1.0]
    const radiusScale = minRadiusScale + 
      ((combinedValue - minCombined) / (maxCombined - minCombined)) * 
      (maxRadiusScale - minRadiusScale);
    
    const radius = baseRadius * Math.max(0.3, Math.min(1.0, radiusScale)); // Clamp between 0.3 and 1.0
    
    const g = svg
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    // Color scale - use provided colors or generate default ones
    const color = d3.scaleOrdinal<string>()
      .domain(filteredData.map(d => d.label))
      .range(filteredData.map(d => d.color || d3.schemeCategory10[filteredData.indexOf(d) % 10]));

    // Create pie generator
    const pie = d3.pie<PieChartData>()
      .value(d => d.value)
      .sort(null);

    // Create arc generator - donut chart with fixed inner radius
    const fixedInnerRadius = baseRadius * 0.25; // Fixed inner radius based on base size
    const arc = d3.arc<d3.PieArcDatum<PieChartData>>()
      .innerRadius(fixedInnerRadius) // Fixed inner radius that doesn't scale
      .outerRadius(Math.max(fixedInnerRadius + 5, radius - 10));

    // Draw background ring showing the range from minimum to maximum radius
    const maxRadius = baseRadius * maxRadiusScale - 10; // Maximum possible radius
    const backgroundArc = d3.arc()
      .innerRadius(fixedInnerRadius)
      .outerRadius(maxRadius)
      .startAngle(0)
      .endAngle(2 * Math.PI);
    
    // Draw background ring
    g.append('path')
      .attr('d', backgroundArc({
        innerRadius: fixedInnerRadius,
        outerRadius: maxRadius,
        startAngle: 0,
        endAngle: 2 * Math.PI
      }))
      .attr('fill', '#E5E5E5')
      .style('opacity', 1.0);

    // Create tooltip
    const tooltip = d3.select('body')
      .append('div')
      .attr('class', 'pie-tooltip')
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', '1000');

    // Create arcs
    const arcs = g.selectAll('.arc')
      .data(pie(filteredData))
      .enter()
      .append('g')
      .attr('class', 'arc');

    // Add paths
    arcs.append('path')
      .attr('d', arc)
      .attr('fill', d => color(d.data.label))
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        // Highlight the slice
        d3.select(this)
          .transition()
          .duration(200)
          .attr('transform', function(d: any) {
            const centroid = arc.centroid(d);
            const x = centroid[0] * 0.1;
            const y = centroid[1] * 0.1;
            return `translate(${x}, ${y})`;
          });

        // Show tooltip
        const percentage = ((d.data.value / d3.sum(filteredData, d => d.value)) * 100).toFixed(1);
        tooltip.transition()
          .duration(200)
          .style('opacity', 0.9);
        
        tooltip.html(`${d.data.label}<br/>${percentage}%`)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function(event, d) {
        // Remove highlight
        d3.select(this)
          .transition()
          .duration(200)
          .attr('transform', 'translate(0, 0)');

        // Hide tooltip
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });

    // Add center text for combined value
    if (combinedItem) {
      // Fixed font size
      const fontSize = 14;
      
      g.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .style('font-size', `${fontSize}px`)
        .style('font-weight', 'bold')
        .style('fill', '#333')
        .text(combinedItem.value.toFixed(3));
    }

    // Cleanup function
    return () => {
      d3.select('body').selectAll('.pie-tooltip').remove();
    };
  }, [data, width, height]);

  if (!data || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-100 rounded-lg"
        style={{ width, height }}
      >
        <span className="text-gray-500 text-xs">No data</span>
      </div>
    );
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className="rounded-lg"
    />
  );
}

export default PieChart;